import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Self,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { handleCustomButtonEdit, rehydrate } from '@campus/external-custom-buttons';
import FroalaEditor from 'froala-editor';
import { getCustomButtonIconPath } from '../../custom-buttons';
import {
  FroalaEventInterface,
  FroalaImageUploadButtons,
  FroalaSettingsInterface,
  FroalaToolBarButtons,
} from '../../interfaces/froala-settings.interface';
import './plugins';

const FroalaDefaults = (FroalaEditor as any).DEFAULTS ?? {};
const CUSTOM_ELEMENT_SANITIZER_CONFIG: Partial<FroalaSettingsInterface> = {
  htmlAllowedTags: [...((FroalaDefaults.htmlAllowedTags as string[]) ?? []), 'dcr-.*', 'dk-.*', 'cds-.*'],
  htmlAllowedEmptyTags: [
    ...((FroalaDefaults.htmlAllowedEmptyTags as string[]) ?? []),
    'dcr-hide-copy-toggle',
    'dcr-divider',
    'dk-mab',
    'dk-number-tile',
    'cds-clock',
  ],
  htmlAllowedAttrs: ['.*'],
  htmlExecuteScripts: false,
};

@Component({
  standalone: false,
  selector: 'campus-froala-editor',
  templateUrl: './froala-editor.component.html',
  styleUrls: ['./froala-editor.component.scss'],
  providers: [],
})
export class FroalaEditorComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() froalaSettingsOverride: Partial<FroalaSettingsInterface>;
  @Input() apiSettings: { apiBase: string; eduContentId: number; eduContentMetadataId: number };
  @Input() hasError: boolean;
  @Input() errorMessage: string;
  @Input() title: string;

  public froalaSettings: FroalaSettingsInterface = { key: '' };
  public model: string;

  get isRequired() {
    return this.controlDirective.control.hasValidator(Validators.required);
  }

  private cdRef = inject(ChangeDetectorRef);
  private cleanupAltClickListener?: () => void;

  constructor(@Self() private controlDirective: NgControl) {
    controlDirective.valueAccessor = this;
  }

  ngOnInit(): void {
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['froalaSettingsOverride'] && !changes['froalaSettingsOverride'].firstChange) {
      this.initialize();
    }
  }

  ngOnDestroy(): void {
    this.cleanupAltClickListener?.();
    this.cleanupAltClickListener = undefined;
  }

  //# region ControlValueAccesor
  onChange = (_) => {};
  onTouched = () => {};

  writeValue(content: string): void {
    this.model = content;
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  //#endregion ControlValueAccesor

  private initialize() {
    const froalaSettingsOverride = this.froalaSettingsOverride;
    const wrapperEvents = this.getEvents();
    const mergedEvents = mergeEvents(wrapperEvents, froalaSettingsOverride?.events);

    if (this.apiSettings) {
      const apiBase = this.apiSettings.apiBase;
      const hasInlineClasses =
        !!froalaSettingsOverride?.inlineClasses && Object.keys(froalaSettingsOverride.inlineClasses).length > 0;
      const hasParagraphStyle =
        !!froalaSettingsOverride?.paragraphStyles && Object.keys(froalaSettingsOverride.paragraphStyles).length > 0;
      const toolbarButtons = this.getToolBarButtons({
        images: true,
        inlineClasses: hasInlineClasses,
        paragraphStyle: hasParagraphStyle,
        customButtons: froalaSettingsOverride?.customButtons,
      });

      this.froalaSettings = {
        language: 'nl',
        key: '',
        imageUploadURL: `${apiBase}/EduFiles/${this.apiSettings.eduContentId}/store`,
        // ImageManager
        requestWithCORS: false,
        requestWithCredentials: true,
        imageManagerPageSize: 20,
        imageManagerScrollOffset: 10,
        imageManagerLoadURL: `${apiBase}/EduFiles/${this.apiSettings.eduContentMetadataId}/list`,
        imageManagerLoadParams: { pageSize: 20 },
        imageManagerDeleteURL: `${apiBase}/EduFiles/${this.apiSettings.eduContentMetadataId}/delete`,
        imageManagerDeleteMethod: 'DELETE',
        imageManagerToggleTags: true,
        attribution: false,
        pastePlain: true,
        heightMax: 400,
        paragraphFormat: {
          N: 'Normaal',
          H4: 'Koptekst 4',
          H5: 'Koptekst 5',
          H6: 'Koptekst 6',
        },
        tableDefaultAlign: 'leftNoWrap',
        imageStyles: {
          '[ corner-m ]': 'Afgerond',
          '[ border border-1 border-on-background ]': 'Omrand',
          '[ clear-both ]': 'Nieuwe lijn',
        },
        imageInsertButtons: [FroalaImageUploadButtons.UPLOAD, FroalaImageUploadButtons.MANAGER],
        toolbarButtons,
        ...CUSTOM_ELEMENT_SANITIZER_CONFIG,
        ...this.froalaSettingsOverride,
        events: {
          ...mergedEvents,
          'image.uploaded': function (response: string) {
            //no new endpoint for uploading hack
            // froala expects a return value with the url back
            // like this {link: "path/to/image.jph"}
            const storageInfo = JSON.parse(response).storageInfo;

            const imgUrl = froalaSettingsOverride?.imageUrl
              ? froalaSettingsOverride.imageUrl(storageInfo)
              : `${apiBase}/EduFiles/${storageInfo.eduFileId}/redirectURL`;

            this.image.insert(imgUrl, false, null, this.image.get(), response);
            return false;
          },
        },
      };
      return;
    }

    this.froalaSettings = {
      toolbarButtons: this.getToolBarButtons({
        images: false,
        inlineClasses: false,
        paragraphStyle: false,
        customButtons: froalaSettingsOverride?.customButtons,
      }),
      events: mergedEvents,
      ...CUSTOM_ELEMENT_SANITIZER_CONFIG,
      ...this.froalaSettings,
      ...this.froalaSettingsOverride,
    };
  }

  private getToolBarButtons(
    { images, inlineClasses, paragraphStyle, customButtons } = {
      images: false,
      inlineClasses: false,
      paragraphStyle: false,
      customButtons: [] as string[] | undefined,
    }
  ): (FroalaToolBarButtons | string)[][] {
    const group1: (FroalaToolBarButtons | string)[] = [
      FroalaToolBarButtons.FULLSCREEN,
      FroalaToolBarButtons.PARAGRAPH_FORMAT,
      paragraphStyle ? FroalaToolBarButtons.PARAGRAPH_STYLE : null,
      FroalaToolBarButtons.TEXT_BOLD,
      FroalaToolBarButtons.TEXT_ITALIC,
      FroalaToolBarButtons.TEXT_UNDERLINE,
      FroalaToolBarButtons.TEXT_STRIKETROUGH,
      FroalaToolBarButtons.TEXT_SUB_SCRIPT,
      FroalaToolBarButtons.TEXT_SUPER_SCRIPT,
      inlineClasses ? FroalaToolBarButtons.INLINE_CLASS : null,
      FroalaToolBarButtons.SPACE,
      FroalaToolBarButtons.TEXT_COLOR,
      FroalaToolBarButtons.TEXT_BACKGROUND,
    ].filter(Boolean);

    const group2: (FroalaToolBarButtons | string)[] = [
      FroalaToolBarButtons.PARAGRAPH_ALIGN,
      FroalaToolBarButtons.LIST_ORDERED,
      FroalaToolBarButtons.LIST_UNORDERED,
      FroalaToolBarButtons.OUTDENT,
      FroalaToolBarButtons.INDENT,
      FroalaToolBarButtons.QUOTE,

      FroalaToolBarButtons.LINK,
      images ? FroalaToolBarButtons.IMAGES : null,
      FroalaToolBarButtons.TABLE,
      FroalaToolBarButtons.SPACE,
      FroalaToolBarButtons.EMOTICONS,
      FroalaToolBarButtons.SPECIAL_CHARS,
    ].filter(Boolean);

    const group3: (FroalaToolBarButtons | string)[] = [
      FroalaToolBarButtons.CLEAR_FORMATTING,
      FroalaToolBarButtons.UNDO,
      FroalaToolBarButtons.REDO,
    ];

    const groups: (FroalaToolBarButtons | string)[][] = [group1, group2, group3];
    if (customButtons?.length) groups.push(customButtons);
    return groups;
  }

  private getEvents(): FroalaEventInterface {
    const self = this;
    return {
      blur: () => {
        this.onTouched();
        this.cdRef.markForCheck();
      },
      initialized: function (scope: any) {
        const editor = scope;
        const editable: HTMLElement | undefined = editor?.$el?.[0];
        if (!editable) return;
        rehydrate(editable);
        self.attachAltClickHandler(editable, editor);
      },
    };
  }

  private attachAltClickHandler(editable: HTMLElement, editor: any): void {
    this.cleanupAltClickListener?.();

    const iconPath = getCustomButtonIconPath();
    const onClick = (e: MouseEvent) => {
      if (!e.altKey) return;
      const target = e.target as HTMLElement;
      if (!target) return;
      const matched = handleCustomButtonEdit(target, {
        iconPath,
        mountTarget: document.body,
        notify: () => editor?.events?.trigger?.('contentChanged'),
        onChange: () => rehydrate(editable),
      });
      if (matched) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    editable.addEventListener('click', onClick);
    this.cleanupAltClickListener = () => editable.removeEventListener('click', onClick);
  }
}

function mergeEvents(wrapper: FroalaEventInterface, consumer?: FroalaEventInterface): FroalaEventInterface {
  if (!consumer) return wrapper;
  const merged: FroalaEventInterface = { ...wrapper, ...consumer };
  if (wrapper.initialized && consumer.initialized) {
    const w = wrapper.initialized;
    const c = consumer.initialized;
    merged.initialized = function (scope: any) {
      w.call(scope);
      c.call(scope);
    } as any;
  }
  return merged;
}
